import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { FileText, CheckCircle, PenTool, AlertCircle, X, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import * as api from '../services/api';

const CONTRACT_TEXT = `ACUERDO DE REFERIDOS – WE ARE DATALAB S.A.S

PARTES
Entre WE ARE DATALAB S.A.S, NIT 900.480.799-9 (en adelante "LA EMPRESA"), y la persona firmante (en adelante "EL REFERENCIADOR").

1. OBJETO
EL REFERENCIADOR podrá generar contactos y reuniones con potenciales clientes para los servicios de LA EMPRESA.

2. PAGOS
LA EMPRESA pagará mensualmente así:
• 1 a 4 citas: $200.000 COP
• 5 a 10 citas: $500.000 COP
• Más de 10 citas: $1.200.000 COP

Adicionalmente, se podrá pagar comisión por negocios cerrados y pagados.

3. COMISIONES POR CIERRE DE NEGOCIOS
EL REFERENCIADOR tendrá derecho a recibir una comisión única por cada negocio cerrado y pagado a LA EMPRESA, de acuerdo con los siguientes rangos:
• Negocios entre $3.000.000 y $20.000.000: $500.000
• Negocios entre $20.000.001 y $50.000.000: $1.000.000
• Negocios entre $50.000.001 y $100.000.000: $2.000.000
• Negocios entre $100.000.001 y $200.000.000: $5.000.000
• Negocios superiores a $200.000.000: $15.000.000

Condiciones:
• La comisión aplica únicamente cuando el negocio haya sido cerrado y pagado en su totalidad a LA EMPRESA.
• LA EMPRESA podrá validar o rechazar contactos.
• No aplican comisiones sobre servicios de administración de pauta.

4. NATURALEZA
Este acuerdo no genera relación laboral.

5. CONFIDENCIALIDAD
La información compartida deberá mantenerse confidencial.

6. VIGENCIA Y TERMINACIÓN
Este acuerdo inicia con la firma y podrá terminarse en cualquier momento por cualquiera de las partes.

WE ARE DATALAB S.A.S
Representante legal`;

const PAYMENT_CONDITIONS = `CONDICIONES PARA PAGO POR COMISIONES A REFERIDOS

WE ARE DATALAB

1. Documentos para vinculación

Las personas que ingresen como referidos o por prestación de servicios deberán enviar al correo laura.ruiz@wearedatalab.co los siguientes documentos:

• Fotocopia de la cédula.
• RUT actualizado.
• Certificación bancaria.

Adicionalmente, deberán incluir en el mismo correo la siguiente información:
• Correo electrónico.
• Número de teléfono.
• Dirección de residencia.

2. Lineamientos para el envío de cuenta de cobro

La cuenta de cobro deberá diligenciarse y enviarse a más tardar el día 20 de cada mes a los siguientes correos:

• aux.contable@wearedatalab.co
• victoria@wearedatalab.co

Adicionalmente, se deberá enviar copia de la cuenta de cobro al correo de la persona encargada del referido, con el fin de que sea revisada y aprobada, y así proceder con el pago correspondiente.

Los pagos se realizarán el día 30 de cada mes, siempre y cuando la cuenta de cobro sea enviada dentro del plazo establecido.

Importante:
• El comprobante de pago de seguridad social deberá adjuntarse únicamente cuando el valor mensual supere un (1) salario mínimo legal mensual vigente, el cual corresponde a $1.750.905.
• La planilla debe ser del mismo mes en que se pasa la cuenta de cobro.
• Si la cuenta de cobro se envía después del día 20, será rechazada, en ese caso, deberá emitirse con fecha del mes siguiente y el pago se comenzará a realizar mes vencido.
• No es posible enviar dos cuentas de cobro dentro del mismo mes.`;

export default function ContractSection({ currentUser, primaryColor }) {
  const [contractStatus, setContractStatus] = useState({ signed: false, signedAt: null });
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [signing, setSigning] = useState(false);
  const [showPaymentConditions, setShowPaymentConditions] = useState(false);
  const sigCanvas = useRef(null);

  useEffect(() => {
    loadContractStatus();
  }, []);

  const loadContractStatus = async () => {
    try {
      const status = await api.getContractStatus();
      setContractStatus(status);
    } catch (err) {
      console.error('Error loading contract status:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleSign = async () => {
    if (!fullName.trim()) {
      toast.error('Ingresa tu nombre completo');
      return;
    }
    if (!documentNumber.trim()) {
      toast.error('Ingresa tu número de documento');
      return;
    }
    if (sigCanvas.current?.isEmpty()) {
      toast.error('Debes firmar el contrato');
      return;
    }

    setSigning(true);
    try {
      const signatureData = sigCanvas.current.toDataURL('image/png');
      await api.signContract({ signatureData, fullName, documentNumber });
      toast.success('Contrato firmado exitosamente');
      setContractStatus({ signed: true, signedAt: new Date().toISOString(), fullName, documentNumber, signatureData });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al firmar el contrato');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Payment Conditions Button */}
      <div className="flex justify-end">
        <Button
          onClick={() => setShowPaymentConditions(true)}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="payment-conditions-btn"
        >
          <DollarSign className="w-4 h-4" />
          Condiciones de Pago
        </Button>
      </div>

      {/* Contract Status */}
      {contractStatus.signed ? (
        <div className="bg-white rounded-xl border border-green-200 p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Contrato Firmado</h2>
          <p className="text-slate-500 mb-1">
            Firmado por: <strong>{contractStatus.fullName}</strong>
          </p>
          {contractStatus.documentNumber && (
            <p className="text-slate-500 mb-1">
              Documento: <strong>{contractStatus.documentNumber}</strong>
            </p>
          )}
          <p className="text-slate-400 text-sm">
            Fecha: {new Date(contractStatus.signedAt).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
          {contractStatus.signatureData && (
            <div className="mt-4 inline-block border border-slate-200 rounded-lg p-3 bg-white">
              <p className="text-xs text-slate-400 mb-1">Firma:</p>
              <img src={contractStatus.signatureData} alt="Firma" className="h-[100px] mx-auto" />
            </div>
          )}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg text-left max-h-[400px] overflow-y-auto">
            <pre className="whitespace-pre-wrap text-xs text-slate-600 font-sans">{CONTRACT_TEXT}</pre>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Contract Header */}
          <div className="p-6 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                <FileText className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Acuerdo de Referidos</h2>
                <p className="text-sm text-slate-500">Lee y firma el contrato para activar tu cuenta como referidor</p>
              </div>
            </div>
          </div>

          {/* Contract Body */}
          <div className="p-6">
            <div className="bg-slate-50 rounded-lg p-6 max-h-[400px] overflow-y-auto mb-6 border border-slate-200">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">{CONTRACT_TEXT}</pre>
            </div>

            {/* Signature Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <PenTool className="w-4 h-4 text-slate-500" />
                <h3 className="font-semibold text-slate-900">Firma del Contrato</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Completo *</label>
                  <Input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Tu nombre completo"
                    data-testid="contract-full-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Número de Documento *</label>
                  <Input
                    value={documentNumber}
                    onChange={(e) => setDocumentNumber(e.target.value)}
                    placeholder="Cédula o NIT"
                    data-testid="contract-document-number"
                  />
                </div>
              </div>

              {/* Signature Pad */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Firma (dibuja con el mouse) *</label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white relative">
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="#1e293b"
                    canvasProps={{
                      className: 'w-full h-[200px] rounded-lg',
                      style: { width: '100%', height: '200px' }
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="absolute top-2 right-2 text-xs text-slate-400 hover:text-red-500 flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Limpiar
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Usa el mouse o tu dedo para firmar en el recuadro</p>
              </div>

              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Al firmar este contrato, aceptas todos los términos y condiciones descritos en el acuerdo de referidos de We Are DataLab S.A.S.
                </p>
              </div>

              <Button
                onClick={handleSign}
                disabled={signing}
                className="w-full py-3 text-white font-semibold"
                style={{ backgroundColor: primaryColor }}
                data-testid="sign-contract-btn"
              >
                {signing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Firmando...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <PenTool className="w-4 h-4" />
                    Firmar Contrato
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Conditions Modal */}
      {showPaymentConditions && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowPaymentConditions(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Condiciones de Pago</h3>
              </div>
              <button onClick={() => setShowPaymentConditions(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">{PAYMENT_CONDITIONS}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
